/**
 * The React surface of the chunk-aware audio player: the provider that owns one
 * engine, the context it publishes, and the hooks that read from it.
 *
 * The engine itself is `./engine` — plain TypeScript, no React — so everything
 * here is wiring: resolve the app seams, hand the engine a live view of them,
 * and keep the command atom mounted for the provider's lifetime.
 *
 * App-specific seams arrive as props:
 * - `userLoggedInAtom` — the app's auth session (`makeUserLoggedInAtom` over
 *   its better-auth client), Initial while the session is still loading.
 * - `resolveAudioChunkSrc` / `readPersistedChunk` / `downloadedAudioAtom` —
 *   mobile's offline downloads.
 * - `isPremiumUser` / `readIsPremiumUser` / `awaitEntitlementReady` — the
 *   app's one entitlement answer (mobile folds the RevenueCat SDK into it).
 *   Absent, the player reads the summary query alone, which is all web has.
 * - `docPackEnabled` — app-level doc-pack kill switch. `useDocPack` and the
 *   engine combine it with the pack version check and the document's download
 *   state, so every surface shows the format the player plays.
 * - `onDocPackLoadFailure` — the app's error reporting, told when a pack
 *   cannot be read and the listener gets chapter audio or a fault instead.
 */
import { RegistryContext, useAtomValue } from '@effect/atom-react';
import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Effect } from 'effect';
import { AsyncResult, Atom, type AtomRegistry } from 'effect/reactivity';
import {
  createContext,
  type PropsWithChildren,
  use,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { latestCell } from '../hooks/use-latest-cell';
import { useAudioDetailedView } from '../query/audio-detail';
import { useAppRuntime, useEq } from '../query/runtime-context';
import type {
  ChapterTtsChunk,
  ChapterTtsChunkParams,
  DocPackTtsChunk,
  DocPackTtsChunkParams,
} from '../query/tts-chunks';
import {
  useUpdateUserPreferencesMutation,
  useUserPreferencesQuery,
  useUserSummaryQuery,
} from '../query/user-hooks';
import { resolveCurrentVoice, useVoicesList } from '../query/voices';
import { useDocPackReadingClassOverrides } from '../stores/doc-pack-reading-preferences-store';
import type { DocPackInfo } from '@listening/api-contract/schemas';
import {
  docPackCatalogArgs,
  usableDocPack,
  type DocPackPageFetchMode,
  type DownloadedAudio,
} from '../doc-pack/source';
import {
  audioChunkCacheAtom,
  docPackChunkCacheKey,
  legacyChunkCacheKey,
  readAudioChunkCacheState,
} from './chunk-cache';
import {
  createPlaybackEngine,
  type AudioChunkSourceResolver,
  type DocPackLoadFailure,
  type PlaybackEngine,
  type PlaybackEngineSeams,
} from './engine';
import {
  createAudioChunkPlayerStoreHandle,
  type AudioChunkPlayerActions,
  type AudioChunkPlayerState,
  type AudioChunkPlayerStoreHandle,
} from './playback-state';
import type { PlaybackCommandAtom } from './playback-command';
import { useAudioPoolPlayerContext } from './pool-player-context';

export * from './chunk-navigation';
export * from './engine';
export * from './playback-command';
export * from './playback-log';
export * from './playback-preferences';
export * from './playback-state';
export * from './playback-stats';
export { getDocPackPlaybackAnchorForPosition } from './timing-index';
export type { DocPackPlaybackAnchor } from './timing-index';
export { isAudioDetailReadyForStreaming } from '../query/audio-detail';

export const AudioChunkPlayerContext = createContext<{
  store: AudioChunkPlayerStoreHandle;
  storeActions: AudioChunkPlayerActions;
  /** The most recent command's outcome, for the rare consumer that needs
   * settlement rather than player state (`useAfterPlaybackCommand`). Commands
   * are latest-wins, so this reports the latest one — not the one a given call
   * site dispatched. */
  commandResult: PlaybackCommandAtom;
  userLoggedIn: boolean;
  docPackEnabled: boolean;
  docPackPageFetchMode: DocPackPageFetchMode;
  downloadedAudioAtom: DownloadedAudioAtom;
  isPremiumUser: boolean | undefined;
  reportDocPackLoadFailure: (failure: DocPackLoadFailure) => void;
} | null>(null);

const renderHolds = new WeakMap<PlaybackEngine, ReadonlyArray<() => void>>();
const holdEngineLanes = (
  registry: AtomRegistry.AtomRegistry,
  engine: PlaybackEngine,
) => {
  const held = renderHolds.get(engine) ?? [
    registry.mount(engine.commandAtom),
    registry.mount(engine.automatedCommandAtom),
    registry.mount(engine.voiceFollowerAtom),
  ];
  renderHolds.set(engine, held);
  return held;
};

type DownloadedAudioAtom = Atom.Atom<Readonly<Record<string, DownloadedAudio>>>;
const noDownloadedAudioAtom: DownloadedAudioAtom = Atom.make({});

export function AudioChunkPlayerProviderInternal({
  children,
  initialAudioId,
  initialLookupAudioId,
  userLoggedInAtom,
  docPackEnabled = true,
  docPackPageFetchMode = 'auto',
  resolveAudioChunkSrc,
  readPersistedChunk,
  readPersistedDocPackChunk,
  downloadedAudioAtom = noDownloadedAudioAtom,
  isPremiumUser,
  readIsPremiumUser,
  awaitEntitlementReady,
  onDocPackLoadFailure,
}: PropsWithChildren & {
  /** Whether a user is signed in, Initial while the app's session is still
   * loading (`makeUserLoggedInAtom`). A load waits on it before deriving its
   * first chunk key, so a signed-in user is never keyed on the signed-out
   * voice. */
  userLoggedInAtom: Atom.Atom<AsyncResult.AsyncResult<boolean>>;
  docPackEnabled?: boolean;
  initialAudioId?: string;
  initialLookupAudioId?: string;
  docPackPageFetchMode?: DocPackPageFetchMode;
  resolveAudioChunkSrc?: AudioChunkSourceResolver;
  downloadedAudioAtom?: DownloadedAudioAtom;
  /** The app's entitlement answer for voice resolution, as a reactive value
   * the app re-renders this provider with. `undefined` falls back to the
   * summary query's `is_active_subscription`. */
  isPremiumUser?: boolean | undefined;
  /** Reads the same answer imperatively. Only the engine calls it, after
   * `awaitEntitlementReady`, so a load sees the answer it waited for even if
   * React has not re-rendered this provider yet. Never read during render: the
   * React Compiler would cache its first result. */
  readIsPremiumUser?: () => boolean;
  /** Resolves once the entitlement answer is real (or the app gave up
   * waiting). A load waits on it before deriving its first chunk key. */
  awaitEntitlementReady?: () => Promise<void>;
  onDocPackLoadFailure?: (failure: DocPackLoadFailure) => void;
  /** Offline fallback (mobile downloads): chunk metadata persisted on device,
   * consulted when the in-memory chunk cache misses. Hits are promoted into
   * the cache so UI reads see them. `audioId` is the canonical document id
   * from the summary, which is how downloads key their records. */
  readPersistedChunk?: (
    params: ChapterTtsChunkParams & { audioId: string },
  ) => ChapterTtsChunk | undefined;
  readPersistedDocPackChunk?: (
    params: DocPackTtsChunkParams,
  ) => DocPackTtsChunk | undefined;
}) {
  const registry = use(RegistryContext);
  const queryClient = useQueryClient();
  const eq = useEq();
  const runtime = useAppRuntime();
  const userLoggedIn = useAtomValue(
    userLoggedInAtom,
    AsyncResult.getOrElse(() => false),
  );

  // One mutable view of the app seams, replaced every render and read — never
  // captured — by the engine.
  const seams: PlaybackEngineSeams = {
    player: useAudioPoolPlayerContext(),
    docPackEnabled,
    docPackPageFetchMode,
    resolveAudioChunkSrc,
    readPersistedChunk,
    readPersistedDocPackChunk,
    downloadedAudioAtom,
    isPremiumUser,
    readIsPremiumUser,
    awaitEntitlementReady,
    onDocPackLoadFailure,
    userLoggedInAtom,
  };
  const [{ store, engine, latestSeams }] = useState(() => {
    const latestSeams = latestCell(seams);
    const store = createAudioChunkPlayerStoreHandle({
      registry,
      initialAudioId,
      initialLookupAudioId,
    });
    const engine = createPlaybackEngine({
      registry,
      runtime,
      queryClient,
      eq,
      store,
      read: latestSeams.get,
    });
    return { store, engine, latestSeams };
  });
  useLayoutEffect(() => {
    // The entitlement is the one voice input that reaches the engine as a
    // prop rather than a query result (those trigger the check themselves).
    const entitlementChanged =
      latestSeams.get().isPremiumUser !== seams.isPremiumUser;
    latestSeams.set(seams);
    if (entitlementChanged) engine.followVoice();
  });

  // An `Atom.fn` node is lazy: an unobserved one is disposed right after the
  // write forces it, which interrupts the very fiber that write started. Both
  // lanes are therefore held mounted for the provider's lifetime — and the
  // first hold is taken during RENDER, not in an effect: React flushes a
  // child's mount effect before the parent's, so a child that dispatches on
  // mount would otherwise force an unmounted atom.
  const renderMounts = useMemo(
    () => holdEngineLanes(registry, engine),
    [registry, engine],
  );
  useEffect(() => {
    const held = [
      registry.mount(engine.commandAtom),
      registry.mount(engine.automatedCommandAtom),
      registry.mount(engine.voiceFollowerAtom),
    ];
    for (const release of renderMounts) release();
    renderHolds.delete(engine);
    return () => {
      for (const release of held) release();
    };
  }, [registry, engine, renderMounts]);

  useUserPreferencesQuery({ enabled: userLoggedIn });
  useVoicesList();
  useAudioDetailedView(useAtomValue(store.atom, (s) => s.audioId));

  // `store` and `engine` are stable; only the primitives change. Memoized so
  // context consumers do not re-render on every provider render.
  const contextValue = useMemo(
    () => ({
      store,
      storeActions: engine.actions,
      commandResult: engine.commandAtom,
      userLoggedIn,
      docPackEnabled,
      docPackPageFetchMode,
      downloadedAudioAtom,
      isPremiumUser,
      reportDocPackLoadFailure: (failure: DocPackLoadFailure) =>
        latestSeams.get().onDocPackLoadFailure?.(failure),
    }),
    [
      store,
      engine,
      userLoggedIn,
      docPackEnabled,
      docPackPageFetchMode,
      downloadedAudioAtom,
      isPremiumUser,
      latestSeams,
    ],
  );

  return (
    <AudioChunkPlayerContext value={contextValue}>
      {children}
    </AudioChunkPlayerContext>
  );
}

/** The loaded document as the player reads it: the decoded audio detail
 * itself, with no remapping layer in between. */
export function useDocumentSummary() {
  const { store } = useContextAndErrorIfNull(AudioChunkPlayerContext);
  const audioId = useAtomValue(store.atom, (state) => state.audioId);
  const audioDetailQuery = useAudioDetailedView(audioId);
  return { documentSummary: audioDetailQuery.data, audioDetailQuery };
}

type DocPackDocument = {
  id: string;
  doc_pack?: DocPackInfo | null | undefined;
};

function useUsableDocPack(document: DocPackDocument | undefined) {
  const { docPackEnabled, downloadedAudioAtom } = useContextAndErrorIfNull(
    AudioChunkPlayerContext,
  );
  const downloaded = useAtomValue(downloadedAudioAtom, (downloads) =>
    document === undefined ? undefined : downloads[document.id],
  );
  return usableDocPack(document?.doc_pack, {
    enabled: docPackEnabled,
    downloaded,
  });
}

function useLogDocPackLoadFailure(
  message: string,
  outcome: 'reader-showed-chapters' | 'reader-page-failed',
  failure: {
    audioId: string | undefined;
    pageNumber?: number;
    isError: boolean;
    error: unknown;
  },
) {
  const runtime = useAppRuntime();
  const report = use(AudioChunkPlayerContext)?.reportDocPackLoadFailure;
  const { audioId, pageNumber, isError, error } = failure;
  useEffect(() => {
    if (!isError) return;
    runtime.runFork(Effect.logWarning(message, { audioId, pageNumber, error }));
    if (audioId !== undefined)
      report?.({ audioId, outcome, pageNumber, error });
  }, [audioId, error, isError, message, outcome, pageNumber, report, runtime]);
}

// A document is in one format at a time, and every surface (readers, search,
// outlines, seeks) shows the format the player is playing once it has loaded
// the document; only before that do they decide for themselves, falling back
// to chapters when the pack's catalog cannot load. A downloaded document never
// falls back: it has no chapter audio.
export function useDocPack(document: DocPackDocument | undefined) {
  const { store, downloadedAudioAtom } = useContextAndErrorIfNull(
    AudioChunkPlayerContext,
  );
  const eq = useEq();
  const usable = useUsableDocPack(document);
  const downloaded = useAtomValue(downloadedAudioAtom, (downloads) =>
    document === undefined ? false : downloads[document.id] !== undefined,
  );
  const playerFormat = useAtomValue(store.atom, (state) =>
    document !== undefined &&
    state.audioId === document.id &&
    (state.docPackChunk !== undefined || state.ttsId !== undefined)
      ? state.playbackMode
      : undefined,
  );
  const wanted = usable !== undefined && playerFormat !== 'legacy';
  const canShowChapters = playerFormat === undefined && !downloaded;
  const queryClient = useQueryClient();
  const audioId = document?.id ?? '';
  const packUrl = usable?.url ?? '';
  const catalogQuery = useQuery({
    ...eq.queryOptions(docPackCatalogArgs(audioId, packUrl)),
    enabled: wanted,
    // With no chapters to fall back to, a failed read stays pending, so keep
    // trying instead of waiting for something else to refetch it.
    refetchInterval: (query) =>
      !canShowChapters && query.state.status === 'error' ? 15_000 : false,
  });
  const failed = wanted && canShowChapters && catalogQuery.isError;
  useLogDocPackLoadFailure(
    'doc-pack catalog failed to load — showing chapters',
    'reader-showed-chapters',
    {
      audioId: document?.id,
      isError: failed,
      error: catalogQuery.error,
    },
  );
  const active = wanted && !failed;
  const pack = active ? catalogQuery.data : undefined;
  /** `pack` once the catalog settles, for pull-based surfaces (CarPlay browse)
   * that cannot re-render when it arrives; undefined means show chapters. */
  const loadPack = useMemo(
    () => () =>
      active
        ? queryClient
            .query({
              ...eq.queryOptions(docPackCatalogArgs(audioId, packUrl)),
              staleTime: 'static',
            })
            .catch(() => undefined)
        : Promise.resolve(undefined),
    [active, audioId, eq, packUrl, queryClient],
  );
  return {
    pack,
    pending: active && pack === undefined,
    active,
    failed,
    loadPack,
  };
}

export function useDocPackPageError(
  audioId: string,
  pageNumber: number,
  pageQuery: { isError: boolean; error: unknown },
) {
  useLogDocPackLoadFailure(
    'doc-pack page failed to load',
    'reader-page-failed',
    {
      audioId,
      pageNumber,
      isError: pageQuery.isError,
      error: pageQuery.error,
    },
  );
}

export function usePlaybackClassOverrides(audioId: string | undefined) {
  const { downloadedAudioAtom } = useContextAndErrorIfNull(
    AudioChunkPlayerContext,
  );
  const downloaded = useAtomValue(downloadedAudioAtom, (downloads) =>
    audioId === undefined ? undefined : downloads[audioId]?.classOverrides,
  );
  const preferred = useDocPackReadingClassOverrides(audioId);
  return downloaded ?? preferred;
}

export const usePreferedVoice = () => {
  const {
    userLoggedIn,
    downloadedAudioAtom,
    isPremiumUser: appIsPremiumUser,
  } = useContextAndErrorIfNull(AudioChunkPlayerContext);
  const prefsMutation = useUpdateUserPreferencesMutation();
  const userPreferencesQuery = useUserPreferencesQuery({
    enabled: userLoggedIn,
  });
  const audioItem = useDocumentSummary().audioDetailQuery.data;
  const audioItemId = audioItem?.id;
  const audioPreferredVoiceName = useAtomValue(
    downloadedAudioAtom,
    (downloads) =>
      audioItemId === undefined ? undefined : downloads[audioItemId]?.voice,
  );
  const userSummaryQuery = useUserSummaryQuery({ enabled: userLoggedIn });
  const isPremiumUser =
    appIsPremiumUser ??
    userSummaryQuery.data?.metering.is_active_subscription === true;
  const voiceOptionsQuery = useVoicesList();
  const voiceOptionsAll = voiceOptionsQuery.data ?? [];

  const preferredVoiceName =
    prefsMutation.isPending && prefsMutation.variables.voice
      ? prefsMutation.variables.voice
      : userPreferencesQuery.data?.voice;

  const resolve = (preferred: string | undefined) =>
    resolveCurrentVoice({
      audioItemLang: audioItem?.language,
      audioPreferredVoiceName,
      preferredVoiceName: preferred,
      isPremiumUser,
      voiceOptionsAll,
    });
  const currentVoice = resolve(preferredVoiceName);
  // What the engine fetches under (engine.ts getVoice reads the cache, not the
  // in-flight mutation), so chunk-cache keys must be derived from this one.
  const committedVoice = resolve(userPreferencesQuery.data?.voice);

  return {
    userPreferencesQuery,
    currentVoice,
    committedVoice,
    prefsMutation,
  };
};

// The voice and pack version are the ones the engine loaded under, so the key
// never names a chunk the engine did not fetch.
export const currentChunkCacheKey = (state: AudioChunkPlayerState) => {
  if (state.loadedVoice === undefined) return undefined;
  if (state.playbackMode === 'doc-pack') {
    return state.audioId &&
      state.docPackChunk &&
      state.loadedDocPackVersion !== undefined
      ? docPackChunkCacheKey({
          audioId: state.audioId,
          docPackVersion: state.loadedDocPackVersion,
          chunkId: state.docPackChunk.id,
          voice: state.loadedVoice,
        })
      : undefined;
  }
  return state.audioId && state.chapterId && state.ttsId
    ? legacyChunkCacheKey({
        chapterId: state.chapterId,
        ttsId: state.ttsId,
        voice: state.loadedVoice,
      })
    : undefined;
};

/**
 * The current chunk as the UI sees it. Read-only: the engine's chunk fetcher
 * is the single fetch authority, and this hook just reads its cache under the
 * key derived from the store's selection — it never issues a request (the
 * previous TanStack version carried a live `queryFn` that could race a
 * duplicate fetch outside the fetcher's single-flight).
 *
 * Keeps the `{ data, isPending, isError }` shape its consumers read, with
 * TanStack's trichotomy: cached data is success (a fault never hides readable
 * chunk text — decision §5 in
 * docs/projects/active/pausing-audio-chunk-playing-refactor/playback-architecture-decisions.md);
 * no data splits into pending vs. error on the player's terminal fault state.
 */
export function useCurrentAudioChunkQuery() {
  const { store } = useContextAndErrorIfNull(AudioChunkPlayerContext);
  // One subscription: the selection only matters as the cache key it produces,
  // so separate field reads would re-render on changes that cannot move it.
  const cacheKey = useAtomValue(store.atom, currentChunkCacheKey);
  const playbackFault = useAtomValue(
    store.atom,
    (state) => state.playbackFault,
  );

  const data = useAtomValue(audioChunkCacheAtom, (state) =>
    cacheKey ? readAudioChunkCacheState(state, cacheKey) : undefined,
  );

  return {
    data,
    isPending: data === undefined && playbackFault === null,
    isError: data === undefined && playbackFault !== null,
  };
}
